import React, { forwardRef, useEffect, useState } from 'react';
import Loading from '../loading/Loading';
import Warning from '../warning/Warning';
import styles from './OrganisationAbnAcn.module.scss';
import abnAcnService from './OrganisationAbnAcnService';
import Select from 'react-select';
import { validate, isCompleted } from '../../../utils/IsComplete';
import ErrorMsg from '../ErrorMsg';
import InfoIcon from '../../../assets/images/info.svg';
import Button from '../button/Button';

const OrganisationAbnAcn = (props, ref) => {
  const SAME_AS_ABOVE = 'Same as Above';
  const defaultTradingName = { value: SAME_AS_ABOVE, label: SAME_AS_ABOVE };
  const [results, setResults] = useState([]);
  const [loaded, setLoaded] = useState(results && results.length);
  const [loading, setLoading] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [currentAbn, setCurrentAbn] = useState(props.value || '');

  const clearResult = () => {
    setResults([]);
    setLoaded(false);
    props.setAbn('');
    props.setAcn('');
    props.setBusinessName('');
    props.setSelectedTradingName('');
  };

  useEffect(() => {
    setShowResult(false);
    clearResult();
  }, [currentAbn]);

  useEffect(() => {
    if (currentAbn) {
      handleLookup(currentAbn);
    }
  }, []);

  const retrieveValue = async searchValue => {
    setResults([]);
    setLoaded(false);
    setLoading(true);
    const searchResponse = await abnAcnService.search(`${props.searchUrl}`, searchValue);
    setShowResult(true);

    if (searchResponse.status === 200) {
      errors.currentAbn = false;
      setResults(searchResponse.data);
      setLoaded(true);
      if (props.setSelectedTradingName) {
        // To reset the default selection in case a new search is performed.
        if (props.selectedTradingName.length === 0) {
          props.setSelectedTradingName(defaultTradingName);
        } else {
          props.setSelectedTradingName(props.selectedTradingName);
        }
      }
    } else {
      setResults([]);
      setLoaded(false);
    }
    setLoading(false);
  };

  const handleLookup = async value => {
    setCurrentAbn(value);
    if (props.setValue) {
      props.setValue(value);
    }
    await retrieveValue(value);
  };

  ///Rules for trading name: 1. show 'Same as above' 1st. 2. combine the tradingName and businessName together from ABR.
  const transformNames = (businessNames, tradingNames) => {
    return [defaultTradingName]
      .concat((businessNames || []).map(label => ({ value: label, label })))
      .concat((tradingNames || []).map(label => ({ value: label, label })));
  };

  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({});
  const validators = [
    {
      fieldName: 'currentAbn',
      fieldValue: currentAbn,
      isValid: v => isCompleted(v) && results.length,
    },
  ];

  useEffect(() => {
    if (currentAbn.length >= 1) {
      setShowErrors(false);
    }
  }, [currentAbn]);

  if (ref) {
    ref.current = {
      validate: () => {
        const isValid = validate(validators, setErrors);
        setShowErrors(!isValid);
        errors.currentAbn = true;
        return isValid;
      },
    };
  }

  return (
    <>
      {props.title && (
        <div className={'row'}>
          <div className={`col-12`}>
            <label className={styles.title} htmlFor={'abnAcnSearchInput'}>
              {props.title}
            </label>
          </div>
        </div>
      )}

      <div className={'row'}>
        <div className={`col-12 col-md-6`}>
          <input
            type="text"
            inputmode="decimal"
            id={'abnAcnSearchInput'}
            className={`${styles.abnAcnBox}`}
            value={currentAbn}
            tabIndex="0"
            onChange={async e => {
              let numericVals = e.target.value.replace(/\D/g, '');
              if (numericVals.length > 11) {
                numericVals = numericVals.substring(0, 10);
              }
              setCurrentAbn(numericVals);
            }}
            onKeyPress={async event => {
              if (event.key === 'Enter') {
                await handleLookup(currentAbn);
              }
            }}
          />

          <Button
            className={'mt-4'}
            secondary
            onClick={async () => {
              await handleLookup(currentAbn);
            }}
          >
            Search
          </Button>
        </div>
      </div>

      {errors.currentAbn && showErrors ? (
        <div className={'row'}>
          <div className={`col-12`}>
            <ErrorMsg type={'error'} description={'Registration Number is required.'} />
          </div>
        </div>
      ) : null}

      {loading && (
        <Loading
          className={`${styles.loading} ${props.title ? styles.loadingWithTitle : ''}`}
          label={false}
          type={'ajax'}
        />
      )}
      {showResult ? (
        <>
          {!!loaded &&
            (results.length > 0 ? (
              results.map(a => (
                <div key={a.abn}>
                  <div className={'row'}>
                    <div className={`col-12`}>
                      <div
                        key={JSON.stringify(a)}
                        className={`${styles.abnBox} abnbox`}
                        id={'abnAcnSearchResult'}
                      >
                        <div className={`d-flex`}>
                          <img src={InfoIcon} className={styles.infoIcon} alt="info icon" />
                          <div>
                            <div className={`${styles.label}`}>
                              {props.label || 'Registered business name'}:
                            </div>
                            <div className={styles.name}>{a.name}</div>
                          </div>
                        </div>
                        {props.setAbn(a.abn)}
                        {props.setAcn(a.acn)}
                        {props.setBusinessName(a.name)}
                      </div>
                    </div>
                  </div>
                  {props.hideTradingNameList && props.hideTradingNameList === true ? null : (
                    <div className={'row'}>
                      <div className={`col-12 ${styles.section}`}>
                        <p className={styles.title}>Registered Trading Name</p>
                        <Select
                          options={transformNames(a.businessNames, a.tradingNames)}
                          value={props.selectedTradingName}
                          onChange={evt => {
                            props.setSelectedTradingName(evt);
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className={'row'}>
                <div className={`col-12`}>
                  <Warning type={'error'} description={props.errorMessage} />
                </div>
              </div>
            ))}
        </>
      ) : null}
    </>
  );
};

export default forwardRef(OrganisationAbnAcn);
